import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Tag, Row, Col,
  Statistic, Modal, Form, Input, Select, Tabs, DatePicker,
  Alert, message,
} from 'antd'
import { SafetyOutlined, CheckOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const API = ''

function fmtINR(v: number) {
  return `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
}

export default function TDSRegisterPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()

  const [period, setPeriod] = useState<dayjs.Dayjs>(dayjs())
  const [sectionFilter, setSectionFilter] = useState<string | undefined>()
  const [challanModal, setChallanModal] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [challanForm] = Form.useForm()

  const { data: tdsData, isLoading: tLoading } = useQuery({
    queryKey: ['tds-deductions', plantId, period?.format('YYYY-MM'), sectionFilter],
    queryFn: () =>
      apiClient.get(`${API}/accounting/tds/deductions`, {
        params: {
          plant_id: plantId,
          month: period?.month() + 1,
          year: period?.year(),
          section_code: sectionFilter || undefined,
          limit: 500,
        },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: tcsData, isLoading: tcLoading } = useQuery({
    queryKey: ['tcs-entries', plantId, period?.format('YYYY-MM')],
    queryFn: () =>
      apiClient.get(`${API}/accounting/tcs/entries`, {
        params: {
          plant_id: plantId,
          month: period?.month() + 1,
          year: period?.year(),
          limit: 500,
        },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: sectionsData } = useQuery({
    queryKey: ['tds-sections'],
    queryFn: () =>
      apiClient.get(`${API}/accounting/tds/sections`).then(r => r.data),
  })

  const challanMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`${API}/accounting/tds/deductions/${id}/challan`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tds-deductions'] })
      message.success('Challan recorded')
      setChallanModal(null)
      challanForm.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const tdsList = Array.isArray(tdsData?.entries ?? tdsData) ? (tdsData?.entries ?? tdsData) : []
  const tcsList = Array.isArray(tcsData?.entries ?? tcsData) ? (tcsData?.entries ?? tcsData) : []
  const sections = Array.isArray(sectionsData) ? sectionsData : []
  const stats = tdsData?.summary ?? {}

  const pendingList = tdsList.filter((t: any) => !t.deposit_challan_no)
  const pendingTotal = pendingList.reduce((s: number, t: any) => s + Number(t.tax_amount ?? 0), 0)

  const tdsColumns = [
    { title: 'Date', dataIndex: 'deduction_date', key: 'deduction_date', width: 100,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YY') : '—' },
    { title: 'Vendor', dataIndex: 'vendor_name', key: 'vendor_name', width: 160, ellipsis: true },
    { title: 'PAN', dataIndex: 'pan', key: 'pan', width: 110,
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v ?? '—'}</span> },
    { title: 'Section', dataIndex: 'section_code', key: 'section_code', width: 90,
      render: (v: string) => v ? <Tag color="blue">{v}</Tag> : '—' },
    { title: 'Base Amount', dataIndex: 'base_amount', key: 'base_amount', width: 120, align: 'right' as const,
      render: (v: number) => fmtINR(v) },
    { title: 'Rate %', dataIndex: 'rate_pct', key: 'rate_pct', width: 80, align: 'right' as const,
      render: (v: number) => `${v ?? 0}%` },
    { title: 'Tax Amount', dataIndex: 'tax_amount', key: 'tax_amount', width: 110, align: 'right' as const,
      render: (v: number) => <b>{fmtINR(v)}</b> },
    { title: 'Challan No', dataIndex: 'deposit_challan_no', key: 'deposit_challan_no', width: 120,
      render: (v: string) => v
        ? <span style={{ fontFamily: 'monospace' }}>{v}</span>
        : <span style={{ color: 'red', fontWeight: 600 }}>PENDING</span> },
    { title: 'Deposit Date', dataIndex: 'deposit_date', key: 'deposit_date', width: 110,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YY') : '—' },
    { title: 'Filed', dataIndex: 'return_filed', key: 'return_filed', width: 70, align: 'center' as const,
      render: (v: boolean) => v ? <CheckOutlined style={{ color: '#52c41a' }} /> : '—' },
    { title: 'Action', key: 'action', width: 120,
      render: (_: unknown, r: any) => !r.deposit_challan_no
        ? <Button size="small" type="primary" onClick={() => { setChallanModal(r); challanForm.setFieldsValue({ deposit_date: dayjs() }) }}>Record Challan</Button>
        : null },
  ]

  const tcsColumns = [
    { title: 'Date', dataIndex: 'collection_date', key: 'collection_date', width: 100,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YY') : '—' },
    { title: 'Customer', dataIndex: 'customer_name', key: 'customer_name', width: 160, ellipsis: true },
    { title: 'PAN', dataIndex: 'pan', key: 'pan', width: 110,
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v ?? '—'}</span> },
    { title: 'Section', dataIndex: 'section_code', key: 'section_code', width: 90,
      render: (v: string) => v ? <Tag color="purple">{v}</Tag> : '—' },
    { title: 'Base Amount', dataIndex: 'base_amount', key: 'base_amount', width: 120, align: 'right' as const,
      render: (v: number) => fmtINR(v) },
    { title: 'Rate %', dataIndex: 'rate_pct', key: 'rate_pct', width: 80, align: 'right' as const,
      render: (v: number) => `${v ?? 0}%` },
    { title: 'Tax Amount', dataIndex: 'tax_amount', key: 'tax_amount', width: 110, align: 'right' as const,
      render: (v: number) => <b>{fmtINR(v)}</b> },
    { title: 'Challan No', dataIndex: 'deposit_challan_no', key: 'deposit_challan_no', width: 120,
      render: (v: string) => v
        ? <span style={{ fontFamily: 'monospace' }}>{v}</span>
        : <span style={{ color: 'red' }}>PENDING</span> },
    { title: 'Filed', dataIndex: 'return_filed', key: 'return_filed', width: 70, align: 'center' as const,
      render: (v: boolean) => v ? <CheckOutlined style={{ color: '#52c41a' }} /> : '—' },
  ]

  const sectionColumns = [
    { title: 'Section', dataIndex: 'section_code', key: 'section_code', width: 100,
      render: (v: string) => <Tag color="blue">{v}</Tag> },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    { title: 'Threshold', dataIndex: 'threshold', key: 'threshold', width: 130, align: 'right' as const,
      render: (v: number) => fmtINR(v) },
    { title: 'Rate %', dataIndex: 'rate_pct', key: 'rate_pct', width: 80, align: 'right' as const,
      render: (v: number) => `${v ?? 0}%` },
    { title: 'Rate % (No PAN)', dataIndex: 'rate_no_pan', key: 'rate_no_pan', width: 120, align: 'right' as const,
      render: (v: number) => v != null ? <span style={{ color: 'red' }}>{v}%</span> : '—' },
  ]

  const tabItems = [
    {
      key: 'deductions',
      label: 'TDS Deductions',
      children: (
        <>
          <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
            <Col>
              <Select value={plantId} onChange={setPlantId} style={{ width: 180 }}
                options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))} />
            </Col>
            <Col>
              <DatePicker picker="month" value={period} onChange={v => v && setPeriod(v)} />
            </Col>
            <Col>
              <Select allowClear placeholder="Section" style={{ width: 130 }} value={sectionFilter}
                onChange={setSectionFilter}
                options={sections.map((s: any) => ({ value: s.section_code, label: s.section_code }))} />
            </Col>
          </Row>

          {pendingList.length > 0 && (
            <Alert
              type="warning"
              message={`${pendingList.length} deduction(s) pending deposit — Total: ${fmtINR(pendingTotal)}`}
              style={{ marginBottom: 16 }}
              showIcon
            />
          )}

          <Row gutter={12} style={{ marginBottom: 16 }}>
            {[
              { title: 'Total Base', value: fmtINR(stats.total_base ?? 0) },
              { title: 'TDS Deducted', value: fmtINR(stats.total_tds ?? 0), color: '#ff4d4f' },
              { title: 'Deposit Pending', value: fmtINR(stats.pending_deposit ?? 0), color: '#faad14' },
              { title: 'Filed', value: fmtINR(stats.filed ?? 0), color: '#52c41a' },
            ].map((s, i) => (
              <Col span={6} key={i}>
                <Card size="small">
                  <Statistic title={s.title} value={s.value} valueStyle={{ fontSize: 14, color: s.color }} />
                </Card>
              </Col>
            ))}
          </Row>

          <Table dataSource={tdsList} columns={tdsColumns} rowKey="id" loading={tLoading}
            size="small" pagination={{ pageSize: 50 }} scroll={{ x: 1100 }} />
        </>
      ),
    },
    {
      key: 'tcs',
      label: 'TCS Entries',
      children: (
        <>
          <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
            <Col>
              <DatePicker picker="month" value={period} onChange={v => v && setPeriod(v)} />
            </Col>
          </Row>
          <Table dataSource={tcsList} columns={tcsColumns} rowKey="id" loading={tcLoading}
            size="small" pagination={{ pageSize: 50 }} scroll={{ x: 900 }} />
        </>
      ),
    },
    {
      key: 'sections',
      label: 'Sections Master',
      children: (
        <Table dataSource={sections} columns={sectionColumns} rowKey="section_code"
          size="small" pagination={false} />
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <SafetyOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        TDS / TCS Register
      </Title>
      <Text type="secondary">Tax deducted/collected at source — deductions, challans and filing</Text>

      <Card style={{ marginTop: 16 }}>
        <Tabs items={tabItems} />
      </Card>

      {/* Record Challan Modal */}
      <Modal
        title={`Record Challan — ${challanModal?.vendor_name}`}
        open={!!challanModal}
        onCancel={() => { setChallanModal(null); challanForm.resetFields() }}
        onOk={() =>
          challanForm.validateFields().then(vals =>
            challanMut.mutate({
              id: challanModal.id,
              data: { ...vals, deposit_date: vals.deposit_date?.format('YYYY-MM-DD') },
            })
          )
        }
        confirmLoading={challanMut.isPending}
      >
        <Form form={challanForm} layout="vertical">
          <Form.Item name="deposit_challan_no" label="Challan Number" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="deposit_date" label="Deposit Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          {challanModal && (
            <Text type="secondary">Tax Amount: <b>{fmtINR(challanModal.tax_amount)}</b></Text>
          )}
        </Form>
      </Modal>
    </div>
  )
}
